/**
 * @fileoverview Typed-name confirmation dialog for destructive deletes.
 * @module ui-admin/ui/ConfirmDeleteModal
 *
 * Responsibilities:
 * - Require the user to type the exact record name before confirming delete.
 * - Copy that name into the confirm field without closing a parent overlay.
 *
 * Dependencies: lucide-react.
 *
 * Architecture: Presentation-only modal. Callers persist the delete.
 *
 * Notes: Clicks inside this dialog must not bubble to a parent overlay
 * (Grid Templates uses an onClick-to-close backdrop).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, X, Copy, Check } from 'lucide-react';

export interface ConfirmDeleteModalProps {
  /** Modal title, e.g. "Delete Template?" */
  title: string;
  /** The display name the user must type to confirm */
  confirmValue: string;
  /** Label shown above the confirm value, e.g. "template name" */
  confirmLabel?: string;
  /** Description paragraph(s) shown below the title */
  description: React.ReactNode;
  /** Optional warning block (e.g., scope warning, cascade warning) */
  warning?: React.ReactNode;
  /** Optional extra content between warning and confirm box (e.g., radio options) */
  extraContent?: React.ReactNode;
  /** Text for the delete button, default "Delete" */
  deleteButtonLabel?: string;
  /** Whether the delete operation is in progress */
  isSubmitting?: boolean;
  /** Called when user confirms (typed name matches) */
  onConfirm: () => void | Promise<void>;
  /** Called when user cancels or clicks backdrop */
  onClose: () => void;
}

export default function ConfirmDeleteModal({
  title,
  confirmValue,
  confirmLabel = 'name',
  description,
  warning,
  extraContent,
  deleteButtonLabel = 'Delete',
  isSubmitting = false,
  onConfirm,
  onClose,
}: ConfirmDeleteModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isValid = confirmText === confirmValue;

  // Auto-focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(confirmValue)
      .then(() => {
        setConfirmText(confirmValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Fallback: just auto-fill without clipboard
        setConfirmText(confirmValue);
      });
  }, [confirmValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 z-110 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center gap-2 bg-red-50">
          <AlertTriangle className="text-red-600 h-5 w-5 shrink-0" />
          <h3 className="font-semibold text-red-900">{title}</h3>
          <button onClick={onClose} className="ml-auto text-red-400 hover:text-red-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Description */}
          <p className="text-sm text-gray-600">{description}</p>

          {/* Optional warning */}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>{warning}</div>
              </div>
            </div>
          )}

          {/* Optional extra content (e.g., radio options for value handling) */}
          {extraContent}

          {/* Confirm box with copy button */}
          <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">To confirm, type the {confirmLabel} below:</p>
            <div className="flex items-center gap-1.5">
              <code className="text-xs font-mono bg-white px-1.5 py-0.5 rounded-sm border select-all flex-1 truncate">
                {confirmValue}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-orange-600 border rounded-sm hover:border-orange-300 transition-colors shrink-0"
                title={`Copy ${confirmLabel}`}
              >
                {copied ? <><Check size={12} className="text-green-600" /> Copied!</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
          </div>

          {/* Text input */}
          <div>
            <input
              ref={inputRef}
              type="text"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder={`Type "${confirmValue}" to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors"
            >
              Discard
            </button>
            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Deleting...' : deleteButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
